import React, {useEffect, useId, useMemo, useRef, useState} from 'react';
import {debounce} from 'lodash';

import {Text, TextArea} from '@albert-io/atomic';

import {announceAnnotationChange} from '../shared/AnnotationAnnouncer';
import {NOTE_MAX_LENGTH} from '../shared/annotationRules';

const NOTE_SAVE_DEBOUNCE_MS = 300;

const NOTE_MAX_LENGTH_TEXT = NOTE_MAX_LENGTH.toLocaleString('en-US');

interface AnnotationNoteProps {
  /** Seeds the draft at mount only — remount (key) to show another mark's note. */
  note: string;
  /** The field's accessible name, naming the marked text the note is on. */
  label: string;
  /**
   * Saves the draft as the student types. Clearing it is held back until the
   * field unmounts, then saved as '' so the note is deleted as the editor closes.
   */
  onNoteChange: (note: string) => void;
}

/**
 * Paste and drop are rejected: the popover portals outside the freeze shield,
 * and notes must be typed (anti-cheat, part-2 spec).
 */
export default function AnnotationNote({note, label, onNoteChange}: AnnotationNoteProps) {
  const fieldId = useId();
  const lengthId = useId();
  const [draft, setDraft] = useState(note);
  const isClearedRef = useRef(false);
  const saveDraft = useMemo(() => debounce(onNoteChange, NOTE_SAVE_DEBOUNCE_MS), [onNoteChange]);
  useEffect(
    () => () => {
      if (isClearedRef.current) {
        saveDraft('');
      }
      saveDraft.flush();
    },
    [saveDraft]
  );
  const length = draft.length.toLocaleString('en-US');

  return (
    <div className='gap-x0-half flex flex-col'>
      <div className='gap-x1 flex items-baseline justify-between'>
        <Text as='label' htmlFor={fieldId} size='xs' color='secondary'>
          Note
        </Text>
        <Text id={lengthId} size='xs' color='secondary'>
          <span aria-hidden>{`${length}/${NOTE_MAX_LENGTH_TEXT}`}</span>
          <span className='sr-only'>{`${length} of ${NOTE_MAX_LENGTH_TEXT} characters`}</span>
        </Text>
      </div>
      <TextArea
        id={fieldId}
        value={draft}
        maxLength={NOTE_MAX_LENGTH}
        aria-label={label}
        aria-describedby={lengthId}
        placeholder='Add note...'
        // Content-sized per design QA on #20493: 28ch wide, grows to 4 lines then scrolls.
        className='field-sizing-content max-h-[4lh] w-[28ch] resize-none'
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          const {value} = event.target;
          if (value.length >= NOTE_MAX_LENGTH && draft.length < NOTE_MAX_LENGTH) {
            announceAnnotationChange(`You've reached the ${NOTE_MAX_LENGTH_TEXT}-character limit.`);
          }
          setDraft(value);
          isClearedRef.current = !value.trim();
          if (isClearedRef.current) {
            // Saving a blank note deletes it, and with it the row this editor may hang from.
            saveDraft.cancel();
          } else {
            saveDraft(value);
          }
        }}
        onBlur={() => saveDraft.flush()}
        onPaste={(event: React.ClipboardEvent) => {
          event.preventDefault();
          announceAnnotationChange("Pasting isn't allowed in notes. Type your note instead.");
        }}
        onDrop={(event: React.DragEvent) => event.preventDefault()}
      />
    </div>
  );
}
